import { motion } from "framer-motion";
import { RotateCcw, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

const RANK_LABELS = ["Your signature", "The alternate", "The wildcard"];
const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1594125311687-3b1b3eafa9f4?auto=format&fit=crop&w=900&q=70";

function priceBand(usd) {
  if (usd < 50) return "Under $50";
  if (usd < 100) return "$50 – $100";
  if (usd < 200) return "$100 – $200";
  if (usd < 400) return "$200 – $400";
  return "$400+";
}

export default function Results({ result, answers, onRestart }) {
  const [top, ...rest] = result.matches;

  const share = async () => {
    const text = `My fragrance matches from Scent Match:\n\n${result.matches
      .map((m, i) => `${i + 1}. ${m.name} — ${m.brand}`)
      .join("\n")}\n\n"${result.signature}"`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Scent Match", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      }
    } catch (e) {
      // user cancelled
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <section
      className="min-h-screen w-full px-6 sm:px-12 lg:px-20 py-12 lg:py-16"
      data-testid="results-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border pb-6">
        <span className="label-caps text-foreground/60">
          Your composition
        </span>
        <div className="flex items-center gap-3">
          <button
            className="btn-sharp"
            onClick={copyLink}
            data-testid="results-copy-link-btn"
          >
            <Copy size={13} strokeWidth={1.5} />
            Copy link
          </button>
          <button
            className="btn-sharp"
            onClick={share}
            data-testid="results-share-btn"
          >
            <Share2 size={13} strokeWidth={1.5} />
            Share
          </button>
        </div>
      </div>

      {/* Signature */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="pt-10 lg:pt-14 max-w-4xl"
      >
        <div className="label-caps" style={{ color: "hsl(var(--gold))" }}>
          Signature
        </div>
        <h1 className="font-serif italic text-3xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-foreground">
          &ldquo;{result.signature}&rdquo;
        </h1>
      </motion.div>

      {/* Top match — hero card */}
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 border-t border-border pt-10"
        data-testid="match-top"
      >
        <div className="lg:col-span-5">
          <div className="aspect-[3/4] border border-border overflow-hidden bg-muted">
            <img
              src={top.image_url || FALLBACK_IMG}
              alt={top.name}
              className="w-full h-full object-cover"
              style={{ filter: "contrast(1.05)" }}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>
        </div>
        <div className="lg:col-span-7 flex flex-col">
          <div className="label-caps text-foreground/50">
            {RANK_LABELS[0]} · {top.match_score}% affinity
          </div>
          <h2 className="font-serif text-4xl sm:text-6xl mt-4 leading-tight text-foreground">
            {top.name}
          </h2>
          <div className="mt-2 label-caps text-foreground/70">
            {top.brand} · {priceBand(top.price_usd)}
          </div>

          <div className="mt-8 max-w-xl text-foreground/80 leading-relaxed text-lg">
            {top.reasoning}
          </div>

          <div className="mt-8">
            <div className="score-track">
              <motion.div
                className="score-fill"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: top.match_score / 100 }}
                transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <div className="label-caps text-foreground/50">Notes</div>
              <div className="mt-2 text-foreground/85 text-sm">
                {top.notes.slice(0, 6).join(" · ")}
              </div>
            </div>
            <div>
              <div className="label-caps text-foreground/50">Families</div>
              <div className="mt-2 text-foreground/85 text-sm">
                {top.families.join(" · ")}
              </div>
            </div>
            <div>
              <div className="label-caps text-foreground/50">For</div>
              <div className="mt-2 text-foreground/85 text-sm">
                {top.occasions.slice(0, 3).join(" · ")}
              </div>
            </div>
            <div>
              <div className="label-caps text-foreground/50">Climate</div>
              <div className="mt-2 text-foreground/85 text-sm">
                {top.climates.join(" · ")}
              </div>
            </div>
          </div>
        </div>
      </motion.article>

      {/* Alternates */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-border pt-10">
        {rest.map((m, i) => (
          <motion.article
            key={m.name}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 + i * 0.15 }}
            className="border border-border p-6 sm:p-8 flex gap-6"
            data-testid={`match-alt-${i}`}
          >
            <div className="w-28 sm:w-40 flex-shrink-0">
              <div className="aspect-[3/4] border border-border overflow-hidden bg-muted">
                <img
                  src={m.image_url || FALLBACK_IMG}
                  alt={m.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMG;
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <div className="label-caps text-foreground/50">
                {RANK_LABELS[i + 1]} · {m.match_score}%
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl mt-2 leading-tight text-foreground truncate">
                {m.name}
              </h3>
              <div className="mt-1 label-caps text-foreground/70 text-[0.65rem]">
                {m.brand} · {priceBand(m.price_usd)}
              </div>
              <p className="mt-4 text-foreground/75 text-sm leading-relaxed">
                {m.reasoning}
              </p>
              <div className="mt-auto pt-4 text-[0.7rem] text-foreground/50 truncate">
                {m.notes.slice(0, 4).join(" · ")}
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      {/* Restart */}
      <div className="mt-16 border-t border-border pt-8 flex flex-wrap items-center justify-between gap-4">
        <div className="text-foreground/50 text-sm max-w-md">
          Not quite right? Rituals are meant to be repeated. Adjust a note or a
          climate and try again.
        </div>
        <button
          className="btn-sharp gold"
          onClick={onRestart}
          data-testid="results-restart-btn"
        >
          <RotateCcw size={14} strokeWidth={1.5} />
          Begin again
        </button>
      </div>
    </section>
  );
}
